import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Table, TableBody, TableCell, TableHead, TableRow, IconButton, styled, TableContainer, Paper, CircularProgress, TextField, Chip, Divider, Grid, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import { subserviceService } from '../../services/subserviceService';
import api from '../../config/axiosConfig';

// Mismas etiquetas/colores que CatalogoServicios.jsx -- mantener consistente.
const RISK_LEVEL_LABELS = { BAJO: 'Bajo', MEDIO: 'Medio', ALTO: 'Alto' };
const RISK_LEVEL_COLORS = {
  BAJO: { bg: '#e8f5e9', text: '#2e7d32' },
  MEDIO: { bg: '#fff3e0', text: '#e65100' },
  ALTO: { bg: '#ffebee', text: '#c62828' }
};

const StyledTableRow = styled(TableRow)({
  '&.selected': {
    backgroundColor: '#ADD8E6 !important',
    '&:hover': {
      backgroundColor: '#ADD8E6 !important'
    }
  }
});


const ModalServicio = ({ open, onClose, service, selectedTab, onSaved }) => {
  //console.log('ModalServicio recibe:', service)
  const [currentTab, setCurrentTab] = useState(selectedTab);
  const [subservices, setSubservices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Información General -- unifica lo que antes era un diálogo aparte (el ícono de lápiz en
  // CatalogoServicios.jsx). Se guarda con su propio botón, independiente de subservicios/
  // cobertura/capacitaciones por defecto (cada uno ya se guarda por su lado).
  const [generalForm, setGeneralForm] = useState({
    serviceName: '', folioPrefix: '', price: '', discount: '', riskLevel: '', active: true
  });
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [generalSuccess, setGeneralSuccess] = useState(null);

  useEffect(() => {
    if (service) {
      setGeneralForm({
        serviceName: service.serviceName || '',
        folioPrefix: service.folioPrefix || '',
        price: (service.price !== undefined && service.price !== null) ? service.price.toString() : '',
        discount: (service.discount !== undefined && service.discount !== null) ? service.discount.toString() : '',
        riskLevel: service.riskLevel || '',
        active: service.active !== undefined ? service.active : true,
      });
    }
  }, [service]);

  const calculateGeneralSubtotal = (price, discount) => {
    const p = parseFloat(price) || 0;
    const d = parseFloat(discount) || 0;
    return (p - p * (d / 100)).toFixed(2);
  };

  const handleSaveGeneral = async () => {
    if (!generalForm.serviceName.trim() || !generalForm.folioPrefix.trim() || !generalForm.price) {
      setGeneralError('Nombre, prefijo del folio y precio son requeridos.');
      return;
    }
    if (isNaN(parseFloat(generalForm.price)) || (generalForm.discount && isNaN(parseFloat(generalForm.discount)))) {
      setGeneralError('Precio y descuento deben ser números válidos.');
      return;
    }
    setSavingGeneral(true);
    setGeneralError(null);
    try {
      const payload = {
        serviceName: generalForm.serviceName.trim(),
        folioPrefix: generalForm.folioPrefix.trim(),
        price: parseFloat(generalForm.price) || 0,
        discount: parseFloat(generalForm.discount) || 0,
        active: generalForm.active,
        iva: 16.0, // IVA fijo 16%
        subtotal: parseFloat(calculateGeneralSubtotal(generalForm.price, generalForm.discount)) || 0,
        riskLevel: generalForm.riskLevel || null,
      };
      const res = await api.put(`/services/${service.id}`, payload);
      setGeneralSuccess('Información general guardada.');
      if (onSaved) onSaved(res.data);
      setTimeout(() => setGeneralSuccess(null), 3000);
    } catch (err) {
      setGeneralError('Error al guardar la información general.');
    } finally {
      setSavingGeneral(false);
    }
  };

  // "Requiere capacitación" vive en el tab de Capacitaciones por Defecto (no en Información
  // General) porque es lo que decide si tiene sentido configurar plantillas aquí. Se guarda con
  // el mismo botón/handler que el resto de Información General (handleSaveGeneral), no al toque.
  // Estado para edición inline
  const [editingIndex, setEditingIndex] = useState(null);
  // Estado para filas expandidas
  const [expandedRows, setExpandedRows] = useState([]);
  // Estado para detalles por fila
  const [detailsByRow, setDetailsByRow] = useState({});
  
  const [coveragePoints, setCoveragePoints] = useState([]);
  const [newPointDescription, setNewPointDescription] = useState("");
  const [savingPoint, setSavingPoint] = useState(false);
  const [coverageError, setCoverageError] = useState(null);

  const [defaultTrainings, setDefaultTrainings] = useState([]);
  const [newDefaultTrainingTopic, setNewDefaultTrainingTopic] = useState("");
  const [newDefaultTrainingDuration, setNewDefaultTrainingDuration] = useState("");
  const [savingDefaultTraining, setSavingDefaultTraining] = useState(false);
  const [defaultTrainingError, setDefaultTrainingError] = useState(null);

  useEffect(() => {
    setCurrentTab(selectedTab);
    if (service && service.id) {
      fetchSubservices();
      fetchCoveragePoints();
      fetchDefaultTrainings();
    }
  }, [selectedTab, open, service]);

  const handleDeleteSubservice = async (subservice) => {
    try {
      setLoading(true);
      await subserviceService.delete(service.id, subservice.id);
      fetchSubservices();
    } catch (err) {
      setError('Error al eliminar el subservicio');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubservices = async () => {
    try {
      setLoading(true);
      if (!service || !service.id) {
        throw new Error('No se ha proporcionado un servicio válido');
      }
      const response = await subserviceService.getAll(service.id);
      // Validar que la respuesta tenga la estructura esperada
      if (response && response.data) {
        setSubservices(response.data.map(subservice => ({
          ...subservice,
          price: subservice.price !== null && subservice.price !== undefined ? parseFloat(subservice.price) : 0,
          discount: subservice.discount !== null && subservice.discount !== undefined ? parseFloat(subservice.discount) : 0
        })));
      } else {
        setSubservices([]);
      }
    } catch (err) {
      console.error('Error en fetchSubservices:', err);
      setError('Error al cargar los subservicios: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Funciones para manejo de documentos
  const fetchCoveragePoints = async () => {
    if (!service || !service.id) return;
    try {
      const res = await api.get("/services/" + service.id + "/coverage-points");
      setCoveragePoints(res.data || []);
    } catch (err) {
      console.error("Error loading coverage points:", err);
    }
  };

  const handleAddCoveragePoint = async () => {
    if (!newPointDescription.trim()) return;
    setSavingPoint(true);
    try {
      const res = await api.post("/services/" + service.id + "/coverage-points", {
        description: newPointDescription.trim(),
        sortOrder: coveragePoints.length,
        included: true
      });
      setCoveragePoints(prev => [...prev, res.data]);
      setNewPointDescription("");
    } catch (err) {
      setCoverageError("Error al agregar punto");
    } finally {
      setSavingPoint(false);
    }
  };

  const handleDeleteCoveragePoint = async (pointId) => {
    try {
      await api.delete("/services/" + service.id + "/coverage-points/" + pointId);
      setCoveragePoints(prev => prev.filter(p => p.id !== pointId));
    } catch (err) {
      setCoverageError("Error al eliminar punto");
    }
  };

  const handleToggleCoveragePoint = async (point) => {
    try {
      const updated = { ...point, included: !point.included };
      await api.put("/services/" + service.id + "/coverage-points/" + point.id, updated);
      setCoveragePoints(prev => prev.map(p => p.id === point.id ? updated : p));
    } catch (err) {
      setCoverageError("Error al actualizar punto");
    }
  };

  // Capacitaciones por defecto del servicio -- plantillas de tema+duración que se
  // ofrecen para precargar el formulario de "Nueva Capacitación" en Capacitaciones.jsx.
  const fetchDefaultTrainings = async () => {
    if (!service || !service.id) return;
    try {
      const res = await api.get("/services/" + service.id + "/default-trainings");
      setDefaultTrainings(res.data || []);
    } catch (err) {
      console.error("Error loading default trainings:", err);
    }
  };

  const handleAddDefaultTraining = async () => {
    if (!newDefaultTrainingTopic.trim()) return;
    setSavingDefaultTraining(true);
    try {
      const res = await api.post("/services/" + service.id + "/default-trainings", {
        trainingTopic: newDefaultTrainingTopic.trim(),
        durationHours: newDefaultTrainingDuration ? parseFloat(newDefaultTrainingDuration) : null,
        sortOrder: defaultTrainings.length
      });
      setDefaultTrainings(prev => [...prev, res.data]);
      setNewDefaultTrainingTopic("");
      setNewDefaultTrainingDuration("");
    } catch (err) {
      setDefaultTrainingError("Error al agregar capacitación por defecto");
    } finally {
      setSavingDefaultTraining(false);
    }
  };

  const handleDeleteDefaultTraining = async (trainingId) => {
    try {
      await api.delete("/services/" + service.id + "/default-trainings/" + trainingId);
      setDefaultTrainings(prev => prev.filter(t => t.id !== trainingId));
    } catch (err) {
      setDefaultTrainingError("Error al eliminar capacitación por defecto");
    }
  };
  // Asegurarse de que el servicio tenga los datos requeridos
  const displayedService = service || {};

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, borderBottom: '1px solid #eef0f3' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2, bgcolor: '#e3f2fd',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <BusinessCenterOutlinedIcon sx={{ color: '#0288d1' }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.25 }}>
              {displayedService.serviceName || 'Detalles del Servicio'}
            </Typography>
            {displayedService.serviceName && (
              <Typography variant="body2" color="text.secondary">
                Folio: {displayedService.folioPrefix || 'N/A'}
                {displayedService.price != null && ` · $${(displayedService.price || 0).toFixed(2)}`}
                {!displayedService.active && ' · Inactivo'}
              </Typography>
            )}
          </Box>
          {displayedService.riskLevel && (
            <Chip
              label={`Riesgo: ${RISK_LEVEL_LABELS[displayedService.riskLevel] || displayedService.riskLevel}`}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: RISK_LEVEL_COLORS[displayedService.riskLevel]?.bg || '#eeeeee',
                color: RISK_LEVEL_COLORS[displayedService.riskLevel]?.text || '#616161'
              }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: '#fafbfc' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            mt: 2, mb: 3, mx: 'auto', maxWidth: '800px',
            bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', minHeight: 44,
            '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' }
          }}
        >
          <Tab label="Información General" />
          <Tab label="Subservicios" />
          <Tab label="Cobertura del Paquete" />
          <Tab label="Capacitaciones por Defecto" />
        </Tabs>

        <Box sx={{ p: 2, mx: 'auto', maxWidth: '800px', width: '100%', bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          {currentTab === 0 && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BusinessCenterOutlinedIcon sx={{ color: '#0288d1', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
                  Información General
                </Typography>
              </Box>

              {generalError && (<Alert severity="error" sx={{ mb: 2 }} onClose={() => setGeneralError(null)}>{generalError}</Alert>)}
              {generalSuccess && (<Alert severity="success" sx={{ mb: 2 }}>{generalSuccess}</Alert>)}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth label="Nombre del Servicio"
                    value={generalForm.serviceName}
                    onChange={(e) => setGeneralForm(p => ({ ...p, serviceName: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth label="Prefijo del Folio"
                    value={generalForm.folioPrefix}
                    onChange={(e) => setGeneralForm(p => ({ ...p, folioPrefix: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth label="Precio" type="number"
                    value={generalForm.price}
                    onChange={(e) => setGeneralForm(p => ({ ...p, price: e.target.value }))}
                    helperText="Precio antes de descuentos e impuestos"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth label="Descuento (%)" type="number"
                    value={generalForm.discount}
                    onChange={(e) => setGeneralForm(p => ({ ...p, discount: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select fullWidth label="Nivel de Riesgo"
                    value={generalForm.riskLevel || ''}
                    onChange={(e) => setGeneralForm(p => ({ ...p, riskLevel: e.target.value }))}
                    helperText="El ente hereda este riesgo al contratar el servicio. 'Sin definir' si no aplica."
                  >
                    <MenuItem value=""><em>Sin definir</em></MenuItem>
                    <MenuItem value="BAJO">Bajo</MenuItem>
                    <MenuItem value="MEDIO">Medio</MenuItem>
                    <MenuItem value="ALTO">Alto</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveGeneral}
                  disabled={savingGeneral}
                  startIcon={savingGeneral ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ borderRadius: 2, boxShadow: 'none' }}
                >
                  {savingGeneral ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </Box>
            </Box>
          )}

          {currentTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ListAltOutlinedIcon sx={{ color: '#0288d1', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
                    Subservicios
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ borderRadius: 2, boxShadow: 'none' }}
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Agregar un subservicio vacío y ponerlo en modo edición inline
                    setSubservices(prev => {
                      // Si ya hay uno vacío en edición, no agregar otro
                      if (prev.some(s => s.id === undefined || s.id === null)) return prev;
                      return [
                        ...prev,
                        {
                          id: null,
                          description: '',
                          price: '',
                          discount: '',
                          active: true,
                          isNew: true // flag para distinguir nuevo
                        }
                      ];
                    });
                    setEditingIndex(subservices.length); // poner en modo edición la nueva fila
                  }}
                >
                  Agregar Subservicio
                </Button>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Precio</TableCell>
                        <TableCell>% de Descuento</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subservices.map((subservice, index) => (
  <React.Fragment key={index}>
    <StyledTableRow>
      {(subservice.isNew || editingIndex === index) ? (
                            <>
                              <TableCell>
                                <input
                                  type="text"
                                  value={subservice.description}
                                  placeholder="Descripción"
                                  style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%' }}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setSubservices(prev => prev.map((s, i) => i === index ? { ...s, description: val } : s));
                                  }}
                                  autoFocus
                                />
                              </TableCell>
                              <TableCell>
                                <input
                                  type="number"
                                  value={subservice.price}
                                  placeholder="Precio"
                                  style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%' }}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setSubservices(prev => prev.map((s, i) => i === index ? { ...s, price: val } : s));
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <input
                                  type="number"
                                  value={subservice.discount}
                                  placeholder="% Descuento"
                                  style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%' }}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setSubservices(prev => prev.map((s, i) => i === index ? { ...s, discount: val } : s));
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                    onClick={() => {
                                      // Cancelar: eliminar la fila nueva
                                      setSubservices(prev => prev.filter((_, i) => i !== index));
                                      setEditingIndex(null);
                                    }}
                                  >
                                    Cancelar
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    sx={{ borderRadius: 2, boxShadow: 'none', textTransform: 'none' }}
                                    onClick={async () => {
                                      const editedSub = subservices[index];
                                      if (editedSub.isNew || !editedSub.id) {
                                        // Alta (POST)
                                        await subserviceService.create(service.id, {
                                          description: editedSub.description,
                                          price: editedSub.price,
                                          discount: Number(editedSub.discount),
                                          active: true
                                        });
                                        setSuccess('Subservicio agregado exitosamente.');
                                      } else {
                                        // Edición (PUT)
                                        await subserviceService.update(service.id, editedSub.id, {
                                          description: editedSub.description,
                                          price: editedSub.price,
                                          discount: Number(editedSub.discount),
                                          active: editedSub.active
                                        });
                                        setSuccess('Subservicio actualizado exitosamente.');
                                      }
                                      fetchSubservices();
                                      setEditingIndex(null);
                                      setTimeout(() => setSuccess(null), 3000);
                                    }}
                                  >
                                    Guardar
                                  </Button>
                                </Box>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>{subservice.description}</TableCell>
                              <TableCell>${subservice.price !== null && subservice.price !== undefined ? parseFloat(subservice.price).toFixed(2) : '0.00'}</TableCell>
                              <TableCell>{subservice.discount !== null && subservice.discount !== undefined ? subservice.discount : '0'}%</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  onClick={() => {
                                    setExpandedRows(prev =>
                                      prev.includes(index)
                                        ? prev.filter(i => i !== index)
                                        : [...prev, index]
                                    );
                                  }}
                                  size="small"
                                  color="primary"
                                >
                                  {expandedRows.includes(index) ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                                </IconButton>
                                <IconButton onClick={() => setEditingIndex(index)} size="small" color="primary">
                                  <EditIcon />
                                </IconButton>
                                <IconButton onClick={() => handleDeleteSubservice(subservice)} size="small" color="error">
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </>
                          )}
    </StyledTableRow>
    {expandedRows.includes(index) && (
      <TableRow>
        <TableCell colSpan={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(detailsByRow[index] || ["Este es un detalle", "Este es un detalle", "Este es un detalle"]).map((detalle, dIdx) => (
              <Box key={dIdx} sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="text"
                  value={detalle}
                  onChange={e => {
                    setDetailsByRow(prev => {
                      const nuevos = { ...prev };
                      nuevos[index] = [...(nuevos[index] || ["Este es un detalle", "Este es un detalle", "Este es un detalle"])];
                      nuevos[index][dIdx] = e.target.value;
                      return nuevos;
                    });
                  }}
                  style={{ border: 'none', background: 'transparent', width: '100%' }}
                />
                <IconButton size="small" color="error" onClick={() => {
                  setDetailsByRow(prev => {
                    const nuevos = { ...prev };
                    nuevos[index] = [...(nuevos[index] || ["Este es un detalle", "Este es un detalle", "Este es un detalle"])];
                    nuevos[index].splice(dIdx, 1);
                    return nuevos;
                  });
                }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#e3f2fd', borderRadius: 1, cursor: 'pointer', width: 'fit-content' }}
              onClick={() => {
                setDetailsByRow(prev => {
                  const nuevos = { ...prev };
                  nuevos[index] = [...(nuevos[index] || ["Este es un detalle", "Este es un detalle", "Este es un detalle"]), "Nuevo detalle"];
                  return nuevos;
                });
              }}
            >
              <AddCircleOutlineIcon color="primary" />
              <span>Agregar</span>
            </Box>
          </Box>
        </TableCell>
      </TableRow>
    )}
  </React.Fragment>
))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {success}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}

          {currentTab === 2 && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FactCheckOutlinedIcon sx={{ color: '#0288d1', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
                    Cobertura del Paquete
                  </Typography>
                </Box>
                {coveragePoints.length > 0 && (
                  <Chip
                    label={`${coveragePoints.filter(p => p.included !== false).length}/${coveragePoints.length} incluidos`}
                    size="small"
                    sx={{ bgcolor: '#e3f2fd', color: '#0288d1', fontWeight: 700, fontSize: '0.72rem' }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Estos puntos aparecen como bullets en las cards de cotización que ve el cliente para este servicio.
              </Typography>

              {coverageError && (<Alert severity="error" sx={{ mb: 2 }} onClose={() => setCoverageError(null)}>{coverageError}</Alert>)}

              {coveragePoints.length === 0 ? (
                <Box sx={{
                  textAlign: 'center', py: 5, px: 2, mb: 2.5,
                  border: '1px dashed #cbd5e1', borderRadius: 2, bgcolor: '#f8fafc'
                }}>
                  <InventoryOutlinedIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Aún no hay puntos de cobertura definidos para este servicio
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                  {coveragePoints.map((point) => {
                    const included = point.included !== false;
                    return (
                      <Box
                        key={point.id}
                        sx={{
                          display: 'flex', alignItems: 'flex-start', gap: 1,
                          p: 1.25, borderRadius: 2, border: '1px solid #e2e8f0',
                          bgcolor: included ? '#fff' : '#f8fafc',
                          transition: 'box-shadow .15s, border-color .15s',
                          '&:hover': { boxShadow: '0 2px 8px rgba(15,23,42,0.06)', borderColor: '#cbd5e1' }
                        }}
                      >
                        <IconButton size="small" onClick={() => handleToggleCoveragePoint(point)} sx={{ p: 0.5 }} title={included ? 'Quitar del paquete' : 'Incluir en el paquete'}>
                          {included
                            ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                            : <RadioButtonUncheckedIcon sx={{ color: '#cbd5e1', fontSize: 20 }} />}
                        </IconButton>
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1, pt: '3px', fontSize: '0.88rem', lineHeight: 1.4,
                            color: included ? '#334155' : 'text.disabled',
                            textDecoration: included ? 'none' : 'line-through'
                          }}
                        >
                          {point.description}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCoveragePoint(point.id)}
                          sx={{ p: 0.5, color: '#cbd5e1', '&:hover': { color: '#e53935', bgcolor: '#fef2f2' } }}
                          title="Eliminar punto"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  label="Nuevo punto de cobertura"
                  value={newPointDescription}
                  onChange={(e) => setNewPointDescription(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddCoveragePoint(); } }}
                  size="small" fullWidth multiline maxRows={3}
                  placeholder="Ej: Incluye revisión de instalaciones eléctricas"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Button
                  variant="contained" size="small"
                  onClick={handleAddCoveragePoint}
                  disabled={savingPoint || !newPointDescription.trim()}
                  startIcon={<AddIcon />}
                  sx={{ whiteSpace: 'nowrap', minWidth: 110, borderRadius: 2, boxShadow: 'none' }}
                >
                  {savingPoint ? "Guardando..." : "Agregar"}
                </Button>
              </Box>
            </Box>
          )}

          {currentTab === 3 && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolOutlinedIcon sx={{ color: '#0288d1', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
                    Capacitaciones por Defecto
                  </Typography>
                </Box>
                {defaultTrainings.length > 0 && (
                  <Chip label={`${defaultTrainings.length} plantilla${defaultTrainings.length !== 1 ? 's' : ''}`} size="small" sx={{ bgcolor: '#e3f2fd', color: '#0288d1', fontWeight: 700, fontSize: '0.72rem' }} />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Precargan Tema y Duración al crear "Nueva Capacitación" en una orden de trabajo de este servicio. Fecha, horario y tutor siempre se capturan al programar.
              </Typography>

              <Box sx={{
                mb: 2.5, p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0',
                bgcolor: defaultTrainings.length > 0 ? '#f1f8f1' : '#f8fafc',
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <SchoolOutlinedIcon sx={{ fontSize: 18, color: defaultTrainings.length > 0 ? '#2e7d32' : '#94a3b8' }} />
                <Typography variant="body2" sx={{ color: defaultTrainings.length > 0 ? '#2e7d32' : 'text.secondary', fontWeight: 600 }}>
                  {defaultTrainings.length > 0
                    ? 'Este paquete requiere capacitación: el ente que lo compre la verá como pendiente en Agenda de Servicios.'
                    : 'Este paquete no requiere capacitación -- agrega al menos una plantilla abajo para que sí la requiera.'}
                </Typography>
              </Box>

              {defaultTrainingError && (<Alert severity="error" sx={{ mb: 2 }} onClose={() => setDefaultTrainingError(null)}>{defaultTrainingError}</Alert>)}

              {defaultTrainings.length === 0 ? (
                <Box sx={{
                  textAlign: 'center', py: 5, px: 2, mb: 2.5,
                  border: '1px dashed #cbd5e1', borderRadius: 2, bgcolor: '#f8fafc'
                }}>
                  <SchoolOutlinedIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Sin capacitaciones por defecto definidas para este servicio
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                  {defaultTrainings.map((t) => (
                    <Box
                      key={t.id}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        p: 1.25, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff',
                        transition: 'box-shadow .15s, border-color .15s',
                        '&:hover': { boxShadow: '0 2px 8px rgba(15,23,42,0.06)', borderColor: '#cbd5e1' }
                      }}
                    >
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '50%', bgcolor: '#e3f2fd',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>
                        <SchoolOutlinedIcon sx={{ fontSize: 17, color: '#0288d1' }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>
                          {t.trainingTopic}
                        </Typography>
                        {t.durationHours != null && (
                          <Typography variant="caption" color="text.secondary">{t.durationHours} hrs</Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDefaultTraining(t.id)}
                        sx={{ p: 0.5, color: '#cbd5e1', '&:hover': { color: '#e53935', bgcolor: '#fef2f2' } }}
                        title="Eliminar plantilla"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  label="Tema de la capacitación" value={newDefaultTrainingTopic}
                  onChange={(e) => setNewDefaultTrainingTopic(e.target.value)}
                  size="small" fullWidth placeholder="Ej: Uso de extintores y evacuación"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  label="Duración (hrs)" type="number" value={newDefaultTrainingDuration}
                  onChange={(e) => setNewDefaultTrainingDuration(e.target.value)}
                  size="small" sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  inputProps={{ min: 0.5, step: 0.5 }}
                />
                <Button
                  variant="contained" size="small"
                  onClick={handleAddDefaultTraining}
                  disabled={savingDefaultTraining || !newDefaultTrainingTopic.trim()}
                  startIcon={<AddIcon />}
                  sx={{ whiteSpace: 'nowrap', minWidth: 110, borderRadius: 2, boxShadow: 'none' }}
                >
                  {savingDefaultTraining ? "Guardando..." : "Agregar"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalServicio;
